// src/components/Layout.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Navigate } from 'react-router-dom';
import LoadingSpinner from './LoadingSpinner';
import { useLanguage } from '../../context/LanguageContext';
import { employeeService } from '../../services/api';
import ConnectionBadge from './ConnectionBadge';
import host from '../../host';

const Layout = ({ children }) => {
    // Move ALL hooks to the top, before any conditional returns
    const {
        isAuthenticated,
        isLoading,
        userRole,
        username,
        logout,
        tenantConfig,
        getBrandName,
        getBrandColor,
        getTenantSetting
    } = useAuth();
    const { language, isRTL, toggleLanguage, t, availableLanguages } = useLanguage();
    const location = useLocation(); // Move this UP
    const navigate = useNavigate(); // Move this UP

    // State hooks
    const [dynamicStyles, setDynamicStyles] = useState('');
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);

    // Effects
    useEffect(() => {
        if (tenantConfig) {
            const theme = tenantConfig.theme || {};
            const primaryColor = theme.primary_color || getBrandColor();
            const secondaryColor = theme.secondary_color || '#1E40AF';
            const accentColor = theme.accent_color || '#60A5FA';
            const textColor = theme.text_color || '#1F2937';
            const backgroundColor = theme.background_color || '#F9FAFB';
            const cardSelectedColor = theme.card_selected_color || '#DBEAFE';
            const cardHoverColor = theme.card_hover_color || '#F3F4F6';

            // Create dynamic CSS with comprehensive coverage
            const styles = `
                :root {
                    --tenant-primary: ${primaryColor};
                    --tenant-secondary: ${secondaryColor};
                    --tenant-accent: ${accentColor};
                    --tenant-text: ${textColor};
                    --tenant-background: ${backgroundColor};
                    --tenant-card-selected: ${cardSelectedColor};
                    --tenant-card-hover: ${cardHoverColor};
                    --tenant-card-normal: ${theme.card_normal_color || '#ffffff'};
                }
                
                /* Primary Button Styles */
                .bg-primary-600 { background-color: ${primaryColor} !important; }
                .bg-primary-700 { background-color: ${secondaryColor} !important; }
                .bg-primary-500 { background-color: ${accentColor} !important; }
                .hover\\:bg-primary-500:hover { background-color: ${accentColor} !important; }
                .hover\\:bg-primary-700:hover { background-color: ${secondaryColor} !important; }
                
                /* Text Colors */
                .text-primary-600 { color: ${primaryColor} !important; }
                .text-primary-700 { color: ${secondaryColor} !important; }
                
                /* Border Colors */
                .border-primary-500 { border-color: ${primaryColor} !important; }
                .border-primary-600 { border-color: ${primaryColor} !important; }
                
                /* Focus States */
                .focus\\:ring-primary-500:focus { --tw-ring-color: ${primaryColor} !important; }
                .focus\\:border-primary-500:focus { border-color: ${primaryColor} !important; }
                
                /* Custom Card Styles */
                .bg-primary-100 { background-color: ${cardSelectedColor} !important; }
                .hover\\:bg-gray-50:hover { background-color: ${cardHoverColor} !important; }
                .bg-white { background-color: ${theme.card_normal_color || '#ffffff'} !important; }
                
                /* Body Background */
                body { background-color: ${backgroundColor} !important; }
                
                /* Navigation Updates */
                .nav-primary { background-color: ${primaryColor} !important; }
            `;

            setDynamicStyles(styles);

            // Apply to document
            let styleElement = document.getElementById('tenant-styles');
            if (!styleElement) {
                styleElement = document.createElement('style');
                styleElement.id = 'tenant-styles';
                document.head.appendChild(styleElement);
            }
            styleElement.textContent = styles;
        }
    }, [tenantConfig, getBrandColor, getTenantSetting]);

    // Helper functions
    const getCurrentPageName = () => {
        switch (location.pathname) {
            case '/cashier-sales': return t('sales_entry');
            case '/products': return t('products');
            case '/employees': return t('employees');
            case '/expenses': return t('expenses');
            case '/settings': return t('settings');
            case '/reporting': return t('reporting');
            case '/bar-orders': return t('bar_orders');
            case '/kitchen-orders': return t('kitchen_orders');
            default: return t('dashboard');
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    // Add this component before the Layout component
    const UserDisplayComponent = () => {
        const {
            username,
            userRole,
            authType,
            employeeId,
            getBrandName
        } = useAuth();

        const [employeeName, setEmployeeName] = useState('');
        const [loading, setLoading] = useState(false);

        // Fetch employee name when authType is 'employee'
        useEffect(() => {
            const fetchEmployeeName = async () => {
                if (authType === 'employee' && employeeId) {
                    setLoading(true);
                    try {
                        const employees = await employeeService.getAllEmployees();
                        const employee = employees.find(emp => emp.employee_id === employeeId);
                        if (employee) {
                            setEmployeeName(employee.name);
                        }
                    } catch (error) {
                        console.error('Failed to fetch employee name:', error);
                        setEmployeeName(username); // Fallback to username
                    }
                    setLoading(false);
                }
            };

            fetchEmployeeName();
        }, [authType, employeeId, username]);

        const getDisplayName = () => {
            if (authType === 'employee') {
                return loading ? t('loading') : (employeeName || username);
            } else if (authType === 'tenant') {
                return getBrandName();
            } else if (authType === 'finance') {
                return username;
            }
            return username || t('user');
        };

        const brandName = getBrandName();

        return (
            <div className="text-white text-sm flex flex-col items-end">
                <div className="font-medium">
                    {getDisplayName()}
                    {userRole === 'finance' && <span className="ml-2 text-xs bg-green-500 px-2 py-1 rounded">{t('finance')}</span>}
                </div>
                <div className="text-xs text-primary-200">
                    {authType === 'employee' ? brandName : (authType === 'tenant' ? t('tenant_admin') : brandName)}
                </div>
            </div>
        );
    };

    // Add this component after UserDisplayComponent
    const BackToFunctionsButton = () => {
        const { authType, logout, setAuthenticatedState, getBrandName } = useAuth();
        const navigate = useNavigate();

        // Don't show the button if we're already on the functions/dashboard page
        if (location.pathname === '/functions' || location.pathname === '/dashboard') {
            return null;
        }

        const handleBackToFunctions = () => {
            if (authType === 'employee') {
                // Revert to tenant admin level
                const tenantId = localStorage.getItem('tenantId');
                const lastUsername = localStorage.getItem('lastUsername') || 'tenant-admin';

                // Clear employee-specific data
                localStorage.removeItem('employee_id');
                localStorage.removeItem('employee_name');
                localStorage.setItem('auth_type', 'tenant');
                localStorage.setItem('user_role', 'admin');
                localStorage.setItem('username', lastUsername);

                // Update auth state
                setAuthenticatedState({
                    isAuthenticated: true,
                    userRole: 'admin',
                    username: lastUsername,
                    tenantId: tenantId,
                    authType: 'tenant'
                });

                navigate('/functions');
            } else {
                // For other auth types, just navigate to functions
                navigate('/functions');
            }
        };

        return (
            <button
                onClick={handleBackToFunctions}
                className="text-white hover:bg-primary-500 px-3 py-2 rounded-md text-sm font-medium"
            >
                {t('back_to_functions')}
            </button>
        );
    };

    // Get tenant-specific values
    const brandName = getBrandName();
    // Fix the logo path for both web and Electron
    const logoSrc = (() => {
        const configLogo = getTenantSetting('brand_logo', '/aqana-logo.png?v=6');

        // Served from file:// — use relative paths
        if (host.usesHashRouter) {
            if (configLogo && !configLogo.startsWith('http')) {
                // Remove leading slash and add ./ for Electron
                const cleanPath = configLogo.startsWith('/') ? configLogo.substring(1) : configLogo;
                return `./${cleanPath}`;
            }
            return './aqana-logo.png?v=6'; // Fallback for Electron
        }

        // For web, ensure absolute path
        if (configLogo && !configLogo.startsWith('/') && !configLogo.startsWith('http')) {
            return `/${configLogo}`;
        }
        return configLogo;
    })();

    const handleImageError = (e) => {
        console.error('Logo image failed to load:', logoSrc);
        e.target.style.display = 'none';
    };

    const handleImageLoad = () => {
        console.log('Logo image loaded successfully:', logoSrc);
    };
    // NOW the conditional returns come AFTER all hooks
    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" />;
    }

    const isCashierPage = location.pathname === '/functions/cashier-sales';

    if (isCashierPage) {
        return (
            <div className="h-screen flex flex-col overflow-hidden bg-gray-100">
                {children}
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Navigation */}
            <nav className="bg-primary-600">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center">
                            <div className="flex-shrink-0 flex items-center">
                                <img
                                    className="h-8 w-auto"
                                    src={logoSrc}
                                    alt={brandName}
                                    onError={handleImageError}
                                    onLoad={handleImageLoad}
                                />
                                {/* Brand name next to logo */}
                                <span className="ml-3 text-white text-lg font-semibold hidden sm:block">
                                    {brandName}
                                </span>
                            </div>
                            <div className="hidden md:block">
                                <div className="ml-10 flex items-baseline space-x-4">
                                    {/* Current Page Display */}
                                    <span className="text-white px-3 py-2 text-sm font-medium">
                                        {getCurrentPageName()}
                                    </span>
                                    {/* Back to Functions Button */}
                                    <BackToFunctionsButton />
                                </div>
                            </div>
                        </div>
                        <div className="hidden md:block">
                            <div className="ml-4 flex items-center md:ml-6">
                                <div className="flex items-center space-x-4">
                                    {/* Tenant info and user display */}
                                    <UserDisplayComponent />


                                    {/* Language Dropdown - Only show if multiple languages available */}
                                    {availableLanguages && availableLanguages.length > 1 && (
                                        <div className="relative">
                                            <button
                                                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                                                className="text-white hover:bg-primary-700 px-3 py-2 rounded-md text-sm font-medium flex items-center space-x-2"
                                                title={t('select_language')}
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9m0 9a9 9 0 01-9-9m9 9c0-5 4-9 9-9m0 0v9" />
                                                </svg>
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>

                                            {isLanguageDropdownOpen && (
                                                <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                                                    <div className="py-1">
                                                        {availableLanguages.map(lang => (
                                                            <button
                                                                key={lang}
                                                                onClick={() => {
                                                                    if (language !== lang) toggleLanguage();
                                                                    setIsLanguageDropdownOpen(false);
                                                                }}
                                                                className={`w-full text-left px-4 py-2 text-sm flex items-center space-x-3 ${language === lang ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-100'
                                                                    }`}
                                                            >
                                                                <span>{lang === 'en' ? 'English' : lang === 'ar' ? 'العربية' : lang.toUpperCase()}</span>
                                                                {language === lang && <span className="ml-auto text-primary-600">✓</span>}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Sign out button */}
                                    <button
                                        onClick={handleLogout}
                                        className="text-white hover:bg-primary-700 px-3 py-2 rounded-md text-sm font-medium"
                                    >
                                        {t('sign_out')}
                                    </button>

                                    {/* Connection status badge */}
                                    <ConnectionBadge />
                                </div>
                            </div>
                        </div>
                        <div className="-mr-2 flex md:hidden">
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className="bg-primary-600 inline-flex items-center justify-center p-2 rounded-md text-white hover:text-white hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-primary-600 focus:ring-white"
                            >
                                <span className="sr-only">{t('open_main_menu')}</span>
                                <svg
                                    className={`${isMenuOpen ? 'hidden' : 'block'} h-6 w-6`}
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M4 6h16M4 12h16M4 18h16"
                                    />
                                </svg>
                                <svg
                                    className={`${isMenuOpen ? 'block' : 'hidden'} h-6 w-6`}
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile menu, show/hide based on menu state */}
                <div className={`${isMenuOpen ? 'block' : 'hidden'} md:hidden`}>
                    <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                        {/* Current Page Display */}
                        <span className="text-white block px-3 py-2 text-base font-medium">
                            {getCurrentPageName()}
                        </span>
                        {/* Back to Functions Button */}
                        <Link
                            to="/functions"
                            className="text-white hover:bg-primary-500 block px-3 py-2 rounded-md text-base font-medium flex items-center justify-between"
                        >
                            <span>{t('back_to_functions')}</span>
                        </Link>
                        {/* Language Toggle - Only show if multiple languages available */}
                        {availableLanguages && availableLanguages.length > 1 && (
                            <div className="space-y-1">
                                {availableLanguages.map(lang => (
                                    <button
                                        key={lang}
                                        onClick={() => {
                                            if (language !== lang) toggleLanguage();
                                        }}
                                        className={`w-full text-left px-3 py-2 rounded-md text-base font-medium flex items-center space-x-3 ${language === lang ? 'bg-primary-500 text-white' : 'text-white hover:bg-primary-500'
                                            }`}
                                    >
                                        <span>{lang === 'en' ? 'English' : lang === 'ar' ? 'العربية' : lang.toUpperCase()}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="pt-4 pb-3 border-t border-primary-700">
                        <div className="flex items-center px-5">
                            <div className="ml-3">
                                <div className="text-base font-medium leading-none text-white">
                                    {username ? username : t('user')}
                                </div>
                                <div className="text-sm font-medium leading-none text-primary-200">
                                    {userRole || ''} • {brandName}
                                </div>
                            </div>
                        </div>
                        <div className="mt-3 px-2 space-y-1">
                            <button
                                onClick={handleLogout}
                                className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-white hover:bg-primary-500"
                            >
                                {t('sign_out')}
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main content */}
            <main>
                <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default Layout;